import { Add } from '@mui/icons-material';
import { Box, Button, Stack } from '@mui/material';
import { GridApi, GridRowId } from '@mui/x-data-grid-pro';
import { MutableRefObject, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import z from 'zod';

import { useContractDemandRequestGridConfig } from '../../ContractDemandRequestWidget.dataGrid';
import { ContractDemandRequestLineItemGridField } from '../../ContractDemandRequestWidget.types';
import { TsrctContractDemandRequestItem } from '@/generated/gql/graphql';
import { I18nNamespace, NegotiationDomainType } from '~/common/types';
import {
  ColumnSelector,
  DataGridWidgetHeader,
  ResetRemoteStateButton,
  useDataGridRemoteState,
} from '~/components/DataGridCustom';
import { useLineItemsGridHandlers } from '~/hooks/useLineItemsGridHandlers/useLineItemsGridHandlers';
import { GridRowClickAwayListener } from '~/routes/IntakeDetail/components/GridRowClickAwayListener/GridRowClickAwayListener';
import { IntakeDataGrid } from '~/routes/IntakeDetail/components/IntakeDataGrid/IntakeDataGrid';
import { useGridValidationStyling } from '~/routes/IntakeDetail/components/IntakeDataGrid/IntakeDataGrid.hooks';
import { IntakeDrawer } from '~/routes/IntakeDetail/components/IntakeDrawer/IntakeDrawer';
import { IntakeDrawerContent } from '~/routes/IntakeDetail/components/IntakeDrawer/IntakeDrawerContent';
import { IntakeDrawerTitle } from '~/routes/IntakeDetail/components/IntakeDrawer/IntakeDrawerTitle';

import { ContractDemandRequestItemForm } from './ContractDemandRequestItemForm';
import { contractDemandRequestItemSchema } from './ContractDemandRequestItemForm.schema';

type ContractDemandRequestLineItemsProps = {
  viewOnly?: boolean;
  apiRef: MutableRefObject<GridApi>;
  lineItems: TsrctContractDemandRequestItem[];
  loading?: boolean;
};

const schema = z.array(contractDemandRequestItemSchema);
const domain = NegotiationDomainType.CONTRACT_DEMAND_REQUEST;

export const ContractDemandRequestLineItems = ({
  viewOnly,
  apiRef,
  lineItems,
  loading,
}: ContractDemandRequestLineItemsProps) => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'ContractDemandRequestLineItems' });
  const { t: tItemForm } = useTranslation(I18nNamespace.modules, { keyPrefix: 'ContractDemandRequestItemForm' });

  const getRowIndex = useCallback(
    (rowId: GridRowId) => {
      const rowIds = apiRef.current.getAllRowIds();
      const rowIndex = rowIds?.findIndex((rId) => rId === rowId);
      return rowIndex;
    },
    [apiRef],
  );

  const { getRowClassName, getGridClassName } = useGridValidationStyling(apiRef, schema);

  const {
    validate,
    currentSelectedRowId,
    setRowSelectionModel,
    selectedLineItem,
    handleLineItemChange,
    handleAddRow,
    handleRowDelete,
    handleCloseDrawer,
  } = useLineItemsGridHandlers({
    apiRef,
    schema,
    rowBuilder: () => {
      return {
        id: v4(),
      };
    },
  });

  const config = useContractDemandRequestGridConfig({ getRowIndex, onRowDelete: handleRowDelete, disabled: viewOnly });

  const {
    gridProps,
    onRemoteStateReset,
    loading: loadingRemoteState,
  } = useDataGridRemoteState({
    apiRef,
    config,
    domain,
  });

  // enforce initial selection state and avoid error coloring
  // temporary fix until proper table validation styling is in place
  useEffect(() => {
    setRowSelectionModel([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems]);

  const rowSelectionModel = currentSelectedRowId ? [currentSelectedRowId] : [];

  // useDataGridRemoteState sets some things via initial-props on the data-grid,
  // therefore the data-grid should not be rendered before the actual initialState was set
  const skip = loadingRemoteState ?? loading;

  return skip ? null : (
    <>
      <GridRowClickAwayListener onClickAway={handleCloseDrawer}>
        <DataGridWidgetHeader
          disablePadding
          right={
            <Stack direction="row">
              <ColumnSelector
                columns={gridProps.columns}
                columnGroupingModel={gridProps.columnGroupingModel}
                columnVisibilityModel={gridProps.columnVisibilityModel}
                onColumnVisibilityModelChange={gridProps.onColumnVisibilityModelChange}
              />
              <ResetRemoteStateButton onRemoteStateReset={onRemoteStateReset} />
            </Stack>
          }
        />

        <IntakeDataGrid
          {...config}
          apiRef={apiRef}
          rows={lineItems}
          getRowHeight={() => 'auto'}
          editMode="row"
          onRowSelectionModelChange={setRowSelectionModel}
          disableRowSelectionOnClick={viewOnly}
          rowSelectionModel={rowSelectionModel}
          sortingMode={'server'}
          getRowClassName={({ id, row }) => {
            return id !== currentSelectedRowId || validate ? getRowClassName(row) : '';
          }}
          className={getGridClassName()}
          {...gridProps}
          initialState={{
            ...gridProps.initialState,
            pinnedColumns: { right: [ContractDemandRequestLineItemGridField.DELETE_ACTION] },
          }}
        />

        <IntakeDrawer isOpen={!!selectedLineItem} handleCloseDrawer={handleCloseDrawer}>
          <IntakeDrawerTitle title={tItemForm('header')} handleCloseDrawer={handleCloseDrawer} />
          <IntakeDrawerContent>
            {selectedLineItem && (
              <ContractDemandRequestItemForm
                key={currentSelectedRowId}
                viewOnly={false}
                lineItem={selectedLineItem}
                onChange={handleLineItemChange}
                validate={validate}
              />
            )}
          </IntakeDrawerContent>
        </IntakeDrawer>
      </GridRowClickAwayListener>
      <Box
        sx={{
          mt: 3,
        }}
      >
        <Button
          disabled={viewOnly}
          variant="text"
          onClick={(e) => {
            e.stopPropagation();
            handleAddRow();
          }}
          startIcon={<Add />}
          dataintercom-target="ContractDemandRequest/LineItems/Add"
        >
          {t('button.addLineItem')}
        </Button>
      </Box>
    </>
  );
};
